import IconButton from "@pathwright/ui/src/components/button/IconButton"
import Link from "@pathwright/ui/src/components/link/Link"
import Screensize from "@pathwright/ui/src/components/ui/Screensize"
import SyncButton from "@pathwright/web/src/modules/path/sync/SyncButton"
import PathDue from "@pathwright/web/src/modules/path/ui/PathDue"
import PathScheduleLabel from "@pathwright/web/src/modules/path/ui/PathScheduleLabel"
import classnames from "classnames"
import { ConnectStores } from "lib/core/store"
import PathModeControls from "library/show/components/path/controls/PathModeControls"
import PathViewAsControl from "library/show/components/path/controls/PathViewAsControl"
import withAvailableModes from "library/show/components/path/withAvailableModes"
import path from "path-browserify"
import { DESIGN, LEARN } from "path/constants"
import { getAttribute } from "../../../../lib/utils/backbone-utils"
import "./styles/StepSequenceNav.css"

const StepSequenceNavArrow = function ({ direction, to, getCanNavigate }) {
  const className = classnames(
    "StepSequenceNavArrow",
    `StepSequenceNavArrow--${direction}`
  )
  return (
    <IconButton
      icon={`chevron-${direction}`}
      className={className}
      inverted={true}
      size={20}
      onClick={() => {
        if (getCanNavigate()) {
          App.navigate(to)
        }
      }}
    />
  )
}

StepSequenceNavArrow.displayName = "StepSequenceNavArrow"

const StepSequenceNavBullet = function ({ step, isActive }) {
  const { progress } = step.completion
  const className = classnames({
    StepSequenceNavBullet: true,
    "StepSequenceNavBullet--complete": progress === 1.0,
    "StepSequenceNavBullet--incomplete": progress < 1.0,
    "StepSequenceNavBullet--active": isActive
  })
  return (
    <Link to={step.url} className={className}>
      <i className="fa fa-circle-o" />
    </Link>
  )
}

const StepSequenceNavBullets = ({ group, activeIndex }) => (
  <div className="StepSequenceNavBullets step-bullets">
    {group.items.map((step, i) => (
      <StepSequenceNavBullet
        key={step.sync_source_id}
        step={step}
        isActive={i === activeIndex}
      />
    ))}
  </div>
)

StepSequenceNavBullet.displayName = "StepSequenceNavBullet"

const StepSequenceNav = function (props) {
  const {
    mode,
    group,
    offering,
    toPrevGroup,
    toNextGroup,
    toPrevStep,
    toNextStep,
    toPath,
    showArrows,
    active_step_index,
    availableModes,
    getCanNavigate
  } = props

  const className = classnames("StepSequenceNav", {
    "StepSequenceNav--shrink": availableModes.length >= 1 && mode > LEARN
  })

  return (
    <Screensize.Consumer>
      {(screensize) => {
        const isMobile = screensize === "sm"
        return (
          <nav className={className}>
            <div className="StepSequenceNav__controls">
              <IconButton to={toPath} size={50} icon="list-back" inverted />
              <PathModeControls />
            </div>
            <div className="StepSequenceNav__wrapper">
              <div className="StepSequenceNav__heading">
                <div className="StepSequenceNav__title-wrapper">
                  {showArrows && !!toPrevGroup && (
                    <StepSequenceNavArrow
                      direction="left"
                      getCanNavigate={getCanNavigate}
                      to={toPrevGroup}
                    />
                  )}
                  <h1 className="StepSequenceNav__title">
                    <Link to={group.url}>{group.name}</Link>
                  </h1>
                  {showArrows && !!toNextGroup && (
                    <StepSequenceNavArrow
                      direction="right"
                      getCanNavigate={getCanNavigate}
                      to={toNextGroup}
                    />
                  )}
                </div>
                <div className="StepSequenceNav__dates">
                  <PathScheduleLabel pathItem={group} inverted showIcon />
                </div>
                <div className="StepSequenceNav__bullets-wrapper">
                  {showArrows && !!toPrevStep && (
                    <StepSequenceNavArrow
                      direction="left"
                      getCanNavigate={getCanNavigate}
                      to={toPrevStep}
                    />
                  )}
                  {group.items.length > 1 && (
                    <StepSequenceNavBullets
                      group={group}
                      activeIndex={active_step_index}
                    />
                  )}
                  {showArrows && !!toNextStep && (
                    <StepSequenceNavArrow
                      direction="right"
                      getCanNavigate={getCanNavigate}
                      to={toNextStep}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="StepSequenceNav__actions">
              <PathViewAsControl
                placement="left"
                transition="transition.slideLeftIn"
              />
              {mode === LEARN && (
                <PathDue isMobile={isMobile} pathItem={group} inverted />
              )}
              {mode === DESIGN && (
                <SyncButton cohortId={offering.id} />
              )}
            </div>
          </nav>
        )
      }}
    </Screensize.Consumer>
  )
}

StepSequenceNav.displayName = "StepSequenceNav"

const STEP_SOURCE_ID_RE = /\/path\/step\/(\d+\/)/

const mapStateToProps = function (state, ownProps) {
  const { step, group, active_step_index } = state.stepStore.state
  const { getPrevStep, getNextStep } = state.stepStore.request
  const { getPrevGroup, getNextGroup } = state.pathStore.request
  const { mode } = state.pathStore.state
  const { offering } = state.resourceStore.state

  // NOTE: this fails to prevent navigation because, while the UI hasn't actually rendered a change (still on same step), the component has the updated data, matching the sourceID in the route...
  const getCanNavigate = () => {
    const stepSourceId = parseInt(
      window.location.pathname.match(STEP_SOURCE_ID_RE)[1]
    )
    // prevent navigating to prev or next step if current step does not match route (stop-gap for sluggish UI on route change)
    const getCanNavigate = stepSourceId === step.sync_source_id

    return getCanNavigate
  }

  const showArrows = mode > LEARN
  const toPath = path.join(step.url, "../../")

  // prev/next group
  const prevGroup = getPrevGroup()
  const toPrevGroup = getAttribute(prevGroup, "url")
  const nextGroup = getNextGroup()
  const toNextGroup = getAttribute(nextGroup, "url")

  // prev/next step
  const prevStep = getPrevStep()
  const toPrevStep = getAttribute(prevStep, "url")
  const nextStep = getNextStep()
  const toNextStep = getAttribute(nextStep, "url")

  return _.assign({}, ownProps, {
    mode,
    step,
    group,
    offering,
    toPath,
    showArrows,
    toPrevGroup,
    toNextGroup,
    toPrevStep,
    toNextStep,
    active_step_index,
    getCanNavigate
  })
}

const stores = [
  { store: "step", watch: ["completion", "active_step_index"] },
  { store: "resource", watch: ["offering"] },
  { store: "path", watch: ["mode"] },
  { store: "navigation", watch: false }
]

export default withAvailableModes(
  ConnectStores(StepSequenceNav, stores, {}, mapStateToProps)
)
